import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import Session from 'supertokens-web-js/recipe/session';
import { useAuthStore } from '@/stores/auth-store';
import { useCartStore } from '@/stores/cart-store';
import { finalizeCompanionHandoff, readStashedHandoff } from '@/lib/companion-handoff';
import { isSafeReturnUrl } from '@/lib/safe-return-url';
import { resolvePostAuthDest } from '@/lib/post-auth-dest';
import api from '@/lib/api';

// Module-level flag survives component unmount/remount (React StrictMode double-mount).
// useRef(false) resets on remount, which allows duplicate OAuth calls.
let callbackProcessed = false;

export default function AuthCallback() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    if (callbackProcessed) return;
    callbackProcessed = true;

    async function processCallback() {
      try {
        await useAuthStore.getState().handleCallback();
        // Cart merge is non-critical — don't let it block login
        try {
          await useCartStore.getState().mergeGuestCart();
        } catch {
          // Silently ignore cart merge failures
        }
        // Referral attribution for OAuth signups. Signup.tsx stashed the
        // ?ref= code in sessionStorage before redirecting to the IdP; consume
        // it now that we have a session. Backend silently no-ops on bad codes
        // and on users older than the attribution window, so this is safe to
        // call unconditionally — the server is the source of truth.
        try {
          const pendingRef = sessionStorage.getItem('pending_ref_code');
          if (pendingRef) {
            sessionStorage.removeItem('pending_ref_code');
            await api.post('/referrals/attribute', { referralCode: pendingRef }).catch(() => {});
          }
        } catch {
          /* sessionStorage unavailable — silent */
        }
        // Clear stale banner dismissal so admin sees maintenance banner
        sessionStorage.removeItem('maintenance-banner-dismissed');
        // Companion-app handoff (contract v1 §6): if the user came here via
        // the mobile/extension flow, finalize and deep-link back to the app
        // instead of going to /admin or /account.
        if (await tryFinishHandoff()) return;
        navigateAfterAuth();
      } catch (err: any) {
        // A duplicate signInAndUp call (StrictMode, race condition) can fail with
        // "invalid_grant" even though the first call already created a valid session.
        // If a session exists, the login succeeded — navigate instead of showing error.
        if (await Session.doesSessionExist()) {
          if (await tryFinishHandoff()) return;
          navigateAfterAuth();
          return;
        }
        setError(err?.message || t('callback.errorFallback'));
      }
    }

    // Honor a redirect stashed before a social-login round-trip (e.g. an
    // invitee who signed in with Google to accept a household invite). Falls
    // back to the resolved post-auth destination (admins → /admin; SaaS
    // subscribers → /, unsubscribed → /pricing; everyone else → /account).
    // Re-validates with isSafeReturnUrl so a tampered sessionStorage value
    // can't open-redirect.
    function navigateAfterAuth() {
      try {
        const pending = sessionStorage.getItem('pending_login_redirect');
        if (pending) {
          sessionStorage.removeItem('pending_login_redirect');
          const safe = isSafeReturnUrl(pending);
          if (safe) {
            if (/^https?:\/\//i.test(safe)) window.location.assign(safe);
            else navigate(safe, { replace: true });
            return;
          }
        }
      } catch {
        /* sessionStorage unavailable — fall through to default */
      }
      navigate(resolvePostAuthDest(useAuthStore.getState().user), { replace: true });
    }

    async function tryFinishHandoff(): Promise<boolean> {
      const stashed = readStashedHandoff();
      if (!stashed) return false;
      try {
        const { redirect_url } = await finalizeCompanionHandoff(stashed);
        window.location.assign(redirect_url);
        return true;
      } catch (err: any) {
        setError(err?.message || t('callback.errorFallback'));
        return true; // we attempted — don't fall through to /admin or /account
      }
    }

    processCallback();
  }, [navigate, t]);

  if (error) {
    return (
      <main className="flex min-h-[calc(100vh-200px)] items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div
            className="rounded-xl border border-red-200 bg-red-50 p-8"
            role="alert"
          >
            <h1 className="text-xl font-bold text-red-900">
              {t('callback.errorHeading')}
            </h1>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <a
              href="/login"
              className="mt-4 inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t('callback.tryAgain')}
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-[calc(100vh-200px)] items-center justify-center px-4 py-12"
      aria-busy="true"
    >
      <div className="text-center">
        <Loader2
          className="mx-auto h-10 w-10 animate-spin text-sage-600"
          aria-hidden="true"
        />
        <h1 className="mt-4 text-xl font-semibold text-stone-900">
          {t('callback.verifying')}
        </h1>
        <p className="mt-2 text-stone-600">
          {t('callback.verifyingSubtitle')}
        </p>
      </div>
    </main>
  );
}
